import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Swords, Target, CheckCircle2, XCircle, Plus, X, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface SuccessCriteriaSummary {
  signal?: string;
  timeframe?: string;
  validators?: string[];
  priority?: string;
}

interface BulletPoint {
  id: string;
  value: string;
}

interface Phase12ConflictInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: { strategyA: string; strategyB: string }) => void;
  previousSuccess?: SuccessCriteriaSummary;
  className?: string;
}

type ConflictQuality = 'none' | 'weak' | 'medium' | 'strong';

const MAX_BULLETS = 2;
const MAX_CHARS = 120;
const CHAR_WARNING_THRESHOLD = 100;

// Härtere, operativere Beispiele
const placeholdersA = [
  "z.B. Zeit, andere Segmente parallel zu testen",
  "z.B. Flexibilität in der Produktstory"
];
const placeholdersB = [
  "z.B. Klare Positionierung im Kernsegment",
  "z.B. Schnelle Validierung einer Kernhypothese"
];

export const Phase12ConflictInput = ({
  onValueChange,
  onStructuredChange,
  previousSuccess,
  className
}: Phase12ConflictInputProps) => {
  const [bulletsA, setBulletsA] = useState<BulletPoint[]>([{ id: '1', value: '' }]);
  const [bulletsB, setBulletsB] = useState<BulletPoint[]>([{ id: '1', value: '' }]);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Combine bullet points for each strategy
  const strategyA = bulletsA.map(b => b.value.trim()).filter(Boolean).join(' • ');
  const strategyB = bulletsB.map(b => b.value.trim()).filter(Boolean).join(' • ');

  useEffect(() => {
    if (strategyA || strategyB) {
      const combined = `Wenn wir Option A wählen, geben wir bewusst auf: ${strategyA}\n\nWenn wir Option B wählen, geben wir bewusst auf: ${strategyB}`;
      onValueChange(combined);
      onStructuredChange?.({ strategyA, strategyB });
    } else {
      onValueChange("");
    }
  }, [strategyA, strategyB, onValueChange, onStructuredChange]);

  const hasSuccessContext = previousSuccess?.signal || previousSuccess?.priority;

  // Add bullet point
  const addBullet = (side: 'A' | 'B') => {
    const newBullet = { id: Date.now().toString(), value: '' };
    if (side === 'A' && bulletsA.length < MAX_BULLETS) {
      setBulletsA([...bulletsA, newBullet]);
    } else if (side === 'B' && bulletsB.length < MAX_BULLETS) {
      setBulletsB([...bulletsB, newBullet]);
    }
  };

  // Remove bullet point
  const removeBullet = (side: 'A' | 'B', id: string) => {
    if (side === 'A' && bulletsA.length > 1) {
      setBulletsA(bulletsA.filter(b => b.id !== id));
    } else if (side === 'B' && bulletsB.length > 1) {
      setBulletsB(bulletsB.filter(b => b.id !== id));
    }
  };

  // Update bullet value
  const updateBullet = (side: 'A' | 'B', id: string, value: string) => {
    const trimmedValue = value.slice(0, MAX_CHARS);
    if (side === 'A') {
      setBulletsA(bulletsA.map(b => b.id === id ? { ...b, value: trimmedValue } : b));
    } else {
      setBulletsB(bulletsB.map(b => b.id === id ? { ...b, value: trimmedValue } : b));
    }
  };

  // Conflict quality check with actionable feedback
  const conflictAnalysis = useMemo(() => {
    const filledA = bulletsA.filter(b => b.value.trim().length > 10);
    const filledB = bulletsB.filter(b => b.value.trim().length > 10);
    
    if (filledA.length === 0 || filledB.length === 0) {
      return { quality: 'none' as ConflictQuality, tip: null };
    }
    
    const vagueTerms = ['allgemein', 'irgendwie', 'vielleicht', 'eventuell', 'möglicherweise', 'könnte', 'würde', 'etwas', 'bisschen', 'gewisse'];
    const allTextA = bulletsA.map(b => b.value.toLowerCase()).join(' ');
    const allTextB = bulletsB.map(b => b.value.toLowerCase()).join(' ');
    
    const hasVagueA = vagueTerms.some(term => allTextA.includes(term));
    const hasVagueB = vagueTerms.some(term => allTextB.includes(term));
    
    const costTerms = ['kosten', 'verlust', 'risiko', 'aufgeben', 'verzichten', 'opfer', 'preis', 'nicht mehr', 'nie wieder', 'verpassen', 'scheitern', 'verlieren', 'schaden', 'blockiert', 'unmöglich'];
    const hasCostA = costTerms.some(term => allTextA.includes(term));
    const hasCostB = costTerms.some(term => allTextB.includes(term));
    
    // Determine quality with specific tips
    if (hasVagueA && hasVagueB) {
      return { 
        quality: 'weak' as ConflictQuality, 
        tip: 'Ersetze "vielleicht" durch konkrete Konsequenzen.'
      };
    }
    if (hasVagueA) {
      return { 
        quality: 'medium' as ConflictQuality, 
        tip: 'Seite A ist noch vage – was genau gibst du auf?'
      };
    }
    if (hasVagueB) {
      return { 
        quality: 'medium' as ConflictQuality, 
        tip: 'Seite B ist noch vage – was genau gibst du auf?'
      };
    }
    if ((hasCostA || filledA.length === 2) && (hasCostB || filledB.length === 2)) {
      return { quality: 'strong' as ConflictQuality, tip: null };
    }
    if (!hasCostA && !hasCostB) {
      return { 
        quality: 'medium' as ConflictQuality, 
        tip: 'Formuliere in Preisen: Was verlierst du unwiederbringlich?'
      };
    }
    
    return { quality: 'medium' as ConflictQuality, tip: 'Schärfe beide Seiten weiter.' };
  }, [bulletsA, bulletsB]);

  const hasBothSides = bulletsA.some(b => b.value.trim().length > 10) && bulletsB.some(b => b.value.trim().length > 10);

  return (
    <div className={cn("space-y-5", className)}>
      {/* Sharpened Header - handlungsorientiert */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            Zwischen welchen zwei Handlungsoptionen musst du dich entscheiden?
          </h3>
          <p className="text-[15px] text-foreground/80 max-w-lg mx-auto leading-relaxed">
            Definiere zwei Wege, die <span className="font-medium text-foreground">nicht gleichzeitig möglich sind</span>.
          </p>
          <p className="text-sm text-muted-foreground italic">
            Wenn du beide Felder ohne Bauchschmerz ausfüllen kannst, ist es kein echter Zielkonflikt.
          </p>
        </div>
      </div>

      {/* Success context reference */}
      {hasSuccessContext && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-primary/5 border border-primary/20 rounded-lg mx-auto max-w-fit">
          <Target className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-sm text-foreground">
            <span className="text-muted-foreground">Erfolgskriterium:</span>{' '}
            <span className="font-medium">{previousSuccess?.priority || previousSuccess?.signal}</span>
          </span>
        </div>
      )}

      {/* Two hard decision cards - SYMMETRIC language */}
      <div className="grid gap-4 lg:grid-cols-[1fr,auto,1fr]">
        {/* Strategy A - Sacrifice Card */}
        <div className={cn(
          "p-5 rounded-xl border-2 transition-all",
          focusedField?.startsWith('A') 
            ? "border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/10" 
            : "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60"
        )}>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-lg font-bold text-amber-600 dark:text-amber-400 flex-shrink-0">
                A
              </span>
              <div className="flex-1">
                <Label className="text-base font-semibold text-foreground block">
                  Wenn wir Option A wählen
                </Label>
                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mt-0.5">
                  geben wir bewusst auf:
                </p>
              </div>
            </div>
            
            {/* Bullet Inputs */}
            <div className="space-y-3">
              {bulletsA.map((bullet, index) => (
                <div key={bullet.id} className="group flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-base font-medium">•</span>
                  <div className="flex-1 relative">
                    <Input
                      value={bullet.value}
                      onChange={(e) => updateBullet('A', bullet.id, e.target.value)}
                      onFocus={() => setFocusedField(`A-${bullet.id}`)}
                      onBlur={() => setFocusedField(null)}
                      placeholder={placeholdersA[index] || placeholdersA[0]}
                      className="text-base h-11 bg-background border-amber-500/30 focus:border-amber-500 focus:ring-amber-500/20 placeholder:text-muted-foreground/60"
                    />
                    {bullet.value.length > CHAR_WARNING_THRESHOLD && (
                      <span className={cn(
                        "absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium",
                        bullet.value.length >= MAX_CHARS ? "text-rose-500" : "text-muted-foreground"
                      )}>
                        {bullet.value.length}/{MAX_CHARS}
                      </span>
                    )}
                  </div>
                  {bulletsA.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-all"
                      onClick={() => removeBullet('A', bullet.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {bulletsA.length < MAX_BULLETS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-sm text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-500/50"
                onClick={() => addBullet('A')}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Weiteren Preis hinzufügen
              </Button>
            )}
          </div>
        </div>

        {/* Visual Divider - Desktop */}
        <div className="hidden lg:flex flex-col items-center justify-center py-4">
          <div className="w-px h-8 bg-border" />
          <div className="w-12 h-12 rounded-full bg-muted border-2 border-border flex items-center justify-center my-2">
            <Swords className="w-5 h-5 text-foreground" />
          </div>
          <div className="w-px h-8 bg-border" />
        </div>
        
        {/* Mobile Divider */}
        <div className="flex lg:hidden items-center justify-center py-2">
          <div className="h-px flex-1 bg-border" />
          <div className="w-10 h-10 rounded-full bg-muted border-2 border-border flex items-center justify-center mx-4">
            <Swords className="w-4 h-4 text-foreground" />
          </div>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Strategy B - Sacrifice Card - SYMMETRIC with A */}
        <div className={cn(
          "p-5 rounded-xl border-2 transition-all",
          focusedField?.startsWith('B') 
            ? "border-rose-500 bg-rose-500/10 shadow-lg shadow-rose-500/10" 
            : "border-rose-500/40 bg-rose-500/5 hover:border-rose-500/60"
        )}>
          <div className="space-y-4">
            {/* Card Header */}
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-lg font-bold text-rose-600 dark:text-rose-400 flex-shrink-0">
                B
              </span>
              <div className="flex-1">
                <Label className="text-base font-semibold text-foreground block">
                  Wenn wir Option B wählen
                </Label>
                <p className="text-sm text-rose-700 dark:text-rose-300 font-medium mt-0.5">
                  geben wir bewusst auf:
                </p>
              </div>
            </div>
            
            {/* Bullet Inputs */}
            <div className="space-y-3">
              {bulletsB.map((bullet, index) => (
                <div key={bullet.id} className="group flex items-center gap-2">
                  <span className="text-rose-600 dark:text-rose-400 text-base font-medium">•</span>
                  <div className="flex-1 relative">
                    <Input
                      value={bullet.value}
                      onChange={(e) => updateBullet('B', bullet.id, e.target.value)}
                      onFocus={() => setFocusedField(`B-${bullet.id}`)}
                      onBlur={() => setFocusedField(null)}
                      placeholder={placeholdersB[index] || placeholdersB[0]}
                      className="text-base h-11 bg-background border-rose-500/30 focus:border-rose-500 focus:ring-rose-500/20 placeholder:text-muted-foreground/60"
                    />
                    {bullet.value.length > CHAR_WARNING_THRESHOLD && (
                      <span className={cn(
                        "absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium",
                        bullet.value.length >= MAX_CHARS ? "text-rose-500" : "text-muted-foreground"
                      )}>
                        {bullet.value.length}/{MAX_CHARS}
                      </span>
                    )}
                  </div>
                  {bulletsB.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-all"
                      onClick={() => removeBullet('B', bullet.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {bulletsB.length < MAX_BULLETS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-sm text-rose-700 dark:text-rose-300 border-rose-500/30 hover:bg-rose-500/10 hover:border-rose-500/50"
                onClick={() => addBullet('B')}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Weiteren Preis hinzufügen
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active Conflict Check with actionable tip */}
      {hasBothSides && (
        <div className={cn(
          "p-4 rounded-xl border-2 transition-all animate-in fade-in slide-in-from-bottom-2 duration-300",
          conflictAnalysis.quality === 'strong' && "bg-emerald-500/10 border-emerald-500/50",
          conflictAnalysis.quality === 'medium' && "bg-amber-500/10 border-amber-500/50",
          conflictAnalysis.quality === 'weak' && "bg-rose-500/10 border-rose-500/50",
        )}>
          <div className="flex items-start gap-3">
            {conflictAnalysis.quality === 'strong' && (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            )}
            {conflictAnalysis.quality === 'medium' && (
              <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            )}
            {conflictAnalysis.quality === 'weak' && (
              <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-base font-semibold",
                conflictAnalysis.quality === 'strong' && "text-emerald-700 dark:text-emerald-300",
                conflictAnalysis.quality === 'medium' && "text-amber-700 dark:text-amber-300",
                conflictAnalysis.quality === 'weak' && "text-rose-700 dark:text-rose-300",
              )}>
                {conflictAnalysis.quality === 'strong' && "✓ Echter Trade-off erkannt"}
                {conflictAnalysis.quality === 'medium' && "Fast geschafft"}
                {conflictAnalysis.quality === 'weak' && "Noch zu vage"}
              </p>
              {conflictAnalysis.tip && (
                <p className="text-sm text-muted-foreground mt-1">
                  {conflictAnalysis.tip}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Requirement hint - replaces collapsible help */}
      <p className="text-sm text-muted-foreground text-center pt-2 border-t border-border/30">
        Beide Seiten müssen ausgefüllt sein. Eine gute Antwort beschreibt einen echten Preis – nicht nur einen Nachteil.
      </p>
    </div>
  );
};
